import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, MoreHorizontal, Briefcase, FolderKanban, Pencil, Trash2, Search, Building2, Users, Handshake, Home, Filter } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = [
  "#0A1628", "#00A7E1", "#3BA2A6", "#22C55E", "#EAB308", "#EF4444", "#8B5CF6", "#EC4899"
];

const CLIENT_TYPES = [
  { value: "investida", label: "Investida", icon: Building2, color: "bg-purple-500" },
  { value: "cliente", label: "Cliente", icon: Users, color: "bg-[#00A7E1]" },
  { value: "parceiro", label: "Parceiro", icon: Handshake, color: "bg-emerald-500" },
  { value: "interno", label: "Interno", icon: Home, color: "bg-amber-500" },
];

const CATEGORIES = [
  "Tecnologia", "Saúde", "Educação", "Finanças", "Varejo", "Indústria", "Serviços", "Outro"
];

const STATUS_OPTIONS = [
  { value: "ativo", label: "Ativo", color: "bg-emerald-500" },
  { value: "inativo", label: "Inativo", color: "bg-gray-500" },
  { value: "em_prospeccao", label: "Em Prospecção", color: "bg-amber-500" },
];

export default function ClientsPage() {
  const { getAuthHeaders, token } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({ 
    name: "", 
    email: "", 
    description: "", 
    color: "#004E8A",
    client_type: "cliente",
    category: "",
    status: "ativo"
  });

  const fetchClients = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/clients`, { headers: getAuthHeaders() });
      setClients(response.data);
    } catch (error) {
      console.error("Fetch clients error:", error);
      toast.error("Erro ao carregar clientes");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (token) {
      fetchClients();
    }
  }, [token, fetchClients]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Nome é obrigatório");
      return;
    }

    try {
      if (editingClient) {
        await axios.put(`${API}/clients/${editingClient.id}`, formData, { headers: getAuthHeaders() });
        toast.success("Cliente atualizado!");
      } else {
        await axios.post(`${API}/clients`, formData, { headers: getAuthHeaders() });
        toast.success("Cliente criado!");
      }
      setDialogOpen(false);
      setEditingClient(null);
      setFormData({ name: "", email: "", description: "", color: "#004E8A", client_type: "cliente", category: "", status: "ativo" });
      fetchClients();
    } catch (error) {
      console.error("Save client error:", error);
      toast.error("Erro ao salvar cliente");
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      email: client.email || "",
      description: client.description || "",
      color: client.color,
      client_type: client.client_type || "cliente",
      category: client.category || "",
      status: client.status || "ativo"
    });
    setDialogOpen(true);
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm("Tem certeza que deseja excluir este cliente? Todos os projetos serão removidos.")) {
      return;
    }
    try {
      await axios.delete(`${API}/clients/${clientId}`, { headers: getAuthHeaders() });
      toast.success("Cliente excluído!");
      fetchClients();
    } catch (error) {
      console.error("Delete client error:", error);
      toast.error("Erro ao excluir cliente");
    }
  };

  const getTypeConfig = (type) => CLIENT_TYPES.find(t => t.value === type) || CLIENT_TYPES[1];
  const getStatusConfig = (status) => STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];

  const filteredClients = clients.filter(c => {
    const matchesSearch = c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (c.email && c.email.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesType = filterType === "all" || c.client_type === filterType;
    const matchesStatus = filterStatus === "all" || c.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  // Group clients by type for summary
  const typesSummary = CLIENT_TYPES.map(type => ({
    ...type,
    count: clients.filter(c => c.client_type === type.value).length
  }));

  if (loading) {
    return (
      <div className="p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-32 bg-muted rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold heading-font">Clientes</h1>
          <p className="text-muted-foreground mt-1">Gerencie seus clientes e projetos</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setEditingClient(null);
            setFormData({ name: "", email: "", description: "", color: "#004E8A", client_type: "cliente", category: "", status: "ativo" });
          }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-[#00A7E1] hover:bg-[#0095c8] text-[#0A1628] font-semibold" data-testid="new-client-btn">
              <Plus className="mr-2 h-4 w-4" />
              Novo Cliente
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <form onSubmit={handleSubmit}>
              <DialogHeader>
                <DialogTitle className="heading-font">{editingClient ? "Editar Cliente" : "Novo Cliente"}</DialogTitle>
                <DialogDescription>
                  {editingClient ? "Atualize as informações do cliente" : "Adicione um novo cliente para começar a criar projetos"}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2 col-span-2">
                    <Label htmlFor="name">Nome *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Nome do cliente"
                      data-testid="client-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="client_type">Tipo</Label>
                    <Select value={formData.client_type} onValueChange={(value) => setFormData({ ...formData, client_type: value })}>
                      <SelectTrigger data-testid="client-type-select">
                        <SelectValue placeholder="Selecione o tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        {CLIENT_TYPES.map(type => (
                          <SelectItem key={type.value} value={type.value}>
                            <div className="flex items-center gap-2">
                              <type.icon className="h-4 w-4" />
                              {type.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                      <SelectTrigger data-testid="client-status-select">
                        <SelectValue placeholder="Selecione o status" />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map(status => (
                          <SelectItem key={status.value} value={status.value}>
                            <div className="flex items-center gap-2">
                              <div className={`h-2 w-2 rounded-full ${status.color}`} />
                              {status.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label htmlFor="category">Categoria</Label>
                    <Select value={formData.category || "none"} onValueChange={(value) => setFormData({ ...formData, category: value === "none" ? "" : value })}>
                      <SelectTrigger data-testid="client-category-select">
                        <SelectValue placeholder="Selecione a categoria" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sem categoria</SelectItem>
                        {CATEGORIES.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="email@exemplo.com"
                      data-testid="client-email-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Descrição</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Descrição do cliente..."
                    data-testid="client-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cor</Label>
                  <div className="flex gap-2 flex-wrap">
                    {COLORS.map((color) => (
                      <button
                        key={color}
                        type="button"
                        className={`h-8 w-8 rounded-full transition-transform ${formData.color === color ? "ring-2 ring-offset-2 ring-accent scale-110" : "hover:scale-110"}`}
                        style={{ backgroundColor: color }}
                        onClick={() => setFormData({ ...formData, color })}
                        data-testid={`color-${color}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" className="bg-[#00A7E1] hover:bg-[#0095c8] text-[#0A1628] font-semibold" data-testid="save-client-btn">
                  {editingClient ? "Salvar" : "Criar Cliente"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Type Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {typesSummary.map(type => (
          <Card 
            key={type.value} 
            className={`cursor-pointer transition-all ${filterType === type.value ? 'ring-2 ring-primary' : 'hover:shadow-md'}`}
            onClick={() => setFilterType(filterType === type.value ? "all" : type.value)}
            data-testid={`filter-type-${type.value}`}
          >
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`p-2 rounded-lg ${type.color}/10`}>
                <type.icon className={`h-5 w-5 ${type.color.replace('bg-', 'text-')}`} />
              </div>
              <div>
                <p className="text-2xl font-bold">{type.count}</p>
                <p className="text-sm text-muted-foreground">{type.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar clientes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-clients-input"
          />
        </div>
        <div className="flex gap-2">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40" data-testid="filter-status-select">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos Status</SelectItem>
              {STATUS_OPTIONS.map(status => (
                <SelectItem key={status.value} value={status.value}>
                  <div className="flex items-center gap-2">
                    <div className={`h-2 w-2 rounded-full ${status.color}`} />
                    {status.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {(filterType !== "all" || filterStatus !== "all") && (
            <Button variant="ghost" size="sm" onClick={() => { setFilterType("all"); setFilterStatus("all"); }}>
              Limpar filtros
            </Button>
          )}
        </div>
      </div>

      {/* Clients Grid */}
      {filteredClients.length === 0 ? (
        <div className="text-center py-16">
          <Briefcase className="h-16 w-16 mx-auto text-muted-foreground/50" />
          <p className="text-lg font-medium mt-4">
            {searchQuery || filterType !== "all" || filterStatus !== "all" ? "Nenhum cliente encontrado" : "Nenhum cliente ainda"}
          </p>
          <p className="text-muted-foreground mt-2">
            {searchQuery || filterType !== "all" || filterStatus !== "all" ? "Tente outros filtros" : "Crie seu primeiro cliente para começar"}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredClients.map((client) => {
            const typeConfig = getTypeConfig(client.client_type);
            const statusConfig = getStatusConfig(client.status);
            const TypeIcon = typeConfig.icon;
            
            return (
              <Card key={client.id} className="group hover:shadow-md transition-shadow overflow-hidden" data-testid={`client-card-${client.id}`}>
                <div className="h-2" style={{ backgroundColor: client.color }} />
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className="h-12 w-12 rounded-full flex items-center justify-center text-white font-bold text-lg"
                        style={{ backgroundColor: client.color }}
                      >
                        {client.name[0].toUpperCase()}
                      </div>
                      <div>
                        <h3 className="font-semibold">{client.name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="secondary" className="text-xs flex items-center gap-1">
                            <TypeIcon className="h-3 w-3" />
                            {typeConfig.label}
                          </Badge>
                          <div className={`h-2 w-2 rounded-full ${statusConfig.color}`} title={statusConfig.label} />
                        </div>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`client-menu-${client.id}`}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEdit(client)} data-testid={`edit-client-${client.id}`}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Editar
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(client.id)} className="text-destructive" data-testid={`delete-client-${client.id}`}>
                          <Trash2 className="mr-2 h-4 w-4" />
                          Excluir
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  {client.category && (
                    <Badge variant="outline" className="mt-3 text-xs">{client.category}</Badge>
                  )}
                  {client.description && (
                    <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{client.description}</p>
                  )}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <FolderKanban className="h-4 w-4" />
                      <span>{client.project_count} projetos</span>
                    </div>
                    <Link to={`/clients/${client.id}/projects`}>
                      <Button variant="outline" size="sm" data-testid={`view-projects-${client.id}`}>
                        Ver Projetos
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
